import { z } from "zod";
import { userIdSchema } from "./tenancy.ts";

// "Resolve with the agent" (openspec change `wiki-resolve-with-agent`, #384): the set's creator
// starts one turn of the set's conversation over the open reviewer threads; the agent revises the
// same set and drafts one reply per thread, which stay drafts until the creator sends them.

/** Threads one run lists at most (D2), oldest first. */
export const WIKI_RESOLVE_THREADS_MAX = 30;
/** Reviewer words one thread contributes to the composed message at most (D8). */
export const WIKI_RESOLVE_THREAD_WORDS_MAX = 2000;
/** A reply's longest text (D3), in code points. */
export const WIKI_AGENT_REPLY_MAX = 1000;
/** A reply's theme (a display grouping only), in code points. */
export const WIKI_AGENT_REPLY_THEME_MAX = 60;
/** A question's choices at most, and each choice's longest text (D6). */
export const WIKI_AGENT_REPLY_CHOICES_MAX = 3;
export const WIKI_AGENT_REPLY_CHOICE_MAX = 80;
/** Threads one Resolve answered call takes at most (D5). */
export const WIKI_RESOLVE_ANSWERED_MAX = 50;

/** The internal reply tool (agent-tools "Thread reply tool is internal"). */
export const WIKI_REPLY_TOOL = "wiki_reply";

export const wikiAgentReplyOutcomeSchema = z.enum(["changed", "question", "no_change"]);
export type WikiAgentReplyOutcome = z.infer<typeof wikiAgentReplyOutcomeSchema>;
export const wikiResolutionRunStatusSchema = z.enum([
  "running",
  "drafted",
  "sent",
  "undone",
  "failed",
]);
export type WikiResolutionRunStatus = z.infer<typeof wikiResolutionRunStatusSchema>;
export const wikiAgentReplyStatusSchema = z.enum(["draft", "sent", "removed"]);
export type WikiAgentReplyStatus = z.infer<typeof wikiAgentReplyStatusSchema>;

/** NFC, trimmed, `min`–`max` code points, no control character (line breaks allowed in text). */
const plain = (max: number, lines: boolean) =>
  z
    .string()
    .max(4 * max)
    .transform((raw) => raw.normalize("NFC").trim())
    .refine(
      (text) =>
        [...text].length >= 1 &&
        [...text].length <= max &&
        !(lines ? /[^\P{Cc}\n\t]/u : /\p{Cc}/u).test(text),
      { message: `1 to ${max} characters` },
    );

/** A reply's text: plain, 1 to 1 000 code points, line breaks allowed. */
export const wikiAgentReplyTextSchema = plain(WIKI_AGENT_REPLY_MAX, true);
/** A reply's theme: one line, 1 to 60 code points. */
export const wikiAgentReplyThemeSchema = plain(WIKI_AGENT_REPLY_THEME_MAX, false);
/** One choice of a question: one line, 1 to 80 code points. */
export const wikiAgentReplyChoiceSchema = plain(WIKI_AGENT_REPLY_CHOICE_MAX, false);

/** A thread reference of the run's composed message (`T1`…`T30`), never an identifier. */
export const wikiThreadRefSchema = z.string().regex(/^T([1-9]|[12][0-9]|30)$/, {
  message: "A thread reference reads T1 to T30, as the request lists them",
});

/**
 * `wiki_reply`'s input (D3): one reply per listed thread, choices only on a question. The model
 * never supplies a link or an identifier.
 */
export const wikiReplyInputSchema = z
  .object({
    ref: wikiThreadRefSchema.describe('The thread\'s reference in the request, as "T1".'),
    theme: wikiAgentReplyThemeSchema.describe(
      "A short theme grouping related comments, at most 60 characters.",
    ),
    outcome: wikiAgentReplyOutcomeSchema.describe(
      "changed: you revised the pages for it; question: you need the reviewer to decide; " +
        "no_change: you did not change anything, and why.",
    ),
    text: wikiAgentReplyTextSchema.describe(
      "The reply to the reviewer in one or two sentences, plain text, at most 1000 characters.",
    ),
    choices: z
      .array(wikiAgentReplyChoiceSchema)
      .min(1)
      .max(WIKI_AGENT_REPLY_CHOICES_MAX)
      .optional()
      .describe("For a question only: up to 3 short answers the reviewer can pick."),
  })
  .refine((r) => r.outcome === "question" || r.choices === undefined, {
    message: "Only a question offers choices",
    path: ["choices"],
  });
export type WikiReplyInput = z.infer<typeof wikiReplyInputSchema>;

/** What the reply tool tells the model: a draft was kept, nothing was sent or resolved. */
export const wikiReplyOutputSchema = z.object({
  status: z.literal("drafted"),
  ref: z.string(),
  note: z.string(),
});
export type WikiReplyOutput = z.infer<typeof wikiReplyOutputSchema>;

/** The run's counts by outcome, and the listed threads the agent left without a reply. */
export const wikiResolutionCountsSchema = z.object({
  changed: z.int().min(0),
  question: z.int().min(0),
  noChange: z.int().min(0),
  unanswered: z.int().min(0),
});
export type WikiResolutionCounts = z.infer<typeof wikiResolutionCountsSchema>;

/**
 * A set's read, for its creator only (D1): whether Resolve with the agent is offered and over how
 * many threads (`count` of `of` when more than 30 are eligible). Absent: no trigger (any other
 * viewer, a set made outside a conversation, or an N-1 API).
 */
export const wikiResolveEligibilitySchema = z.object({
  eligible: z.boolean(),
  count: z.int().min(0).max(WIKI_RESOLVE_THREADS_MAX),
  of: z.int().min(0),
  /** A run of the set is in progress (its turn has not ended). */
  running: z.boolean().optional(),
});
export type WikiResolveEligibility = z.infer<typeof wikiResolveEligibilitySchema>;

/**
 * The set's latest settled run, for the decision bar (D7): its revision number, status and counts
 * by outcome. No text. Absent: no run, or an N-1 API.
 */
export const wikiAgentRevisionSchema = z.object({
  runId: z.uuid(),
  revision: z.int().min(1),
  status: wikiResolutionRunStatusSchema,
  counts: wikiResolutionCountsSchema,
});
export type WikiAgentRevision = z.infer<typeof wikiAgentRevisionSchema>;

/**
 * An agent reply as a viewer of its thread reads it (D10): the reply, or `hidden` when its label
 * holds a source they cannot read (no outcome, text or choice then).
 */
export const wikiAgentReplyViewSchema = z.discriminatedUnion("hidden", [
  z.object({
    hidden: z.literal(false),
    id: z.uuid(),
    runId: z.uuid(),
    revision: z.int().min(1),
    /** "The agent, for <name>": the run's creator. */
    forUserId: userIdSchema,
    forName: z.string().nullable(),
    theme: z.string(),
    outcome: wikiAgentReplyOutcomeSchema,
    text: z.string(),
    choices: z.array(z.string()),
    status: wikiAgentReplyStatusSchema,
    edited: z.boolean(),
    createdAt: z.string(),
    sentAt: z.string().nullable(),
    /** A `changed` reply touched the thread's item: "See the change" against the run's snapshot. */
    changeOf: z.object({ itemId: z.uuid(), beforeHash: z.string() }).nullable(),
  }),
  z.object({
    hidden: z.literal(true),
    id: z.uuid(),
    forUserId: userIdSchema,
    forName: z.string().nullable(),
    sentAt: z.string().nullable(),
  }),
]);
export type WikiAgentReplyView = z.infer<typeof wikiAgentReplyViewSchema>;

/**
 * A thread's agent state (D6, D10): its replies as the viewer reads them (sent ones, plus the
 * viewer's own run drafts), and who it waits on when the latest sent reply is a question nobody
 * but the creator answered yet.
 */
export const wikiThreadAgentStateSchema = z.object({
  replies: z.array(wikiAgentReplyViewSchema),
  /** The reviewer a question waits on; absent: not waiting. */
  waitingFor: z
    .object({
      userId: userIdSchema,
      name: z.string().nullable(),
      /**
       * Set only on the waited-for reviewer's own read: the question is hidden from them, so
       * they ask the creator instead (D10). Never tells anyone what another person can read.
       */
      hiddenFromThem: z.boolean(),
    })
    .optional(),
});
export type WikiThreadAgentState = z.infer<typeof wikiThreadAgentStateSchema>;

/** A drafted reply in the run's review (D4), for the run's creator. */
export const wikiResolutionReplySchema = z.object({
  id: z.uuid(),
  ref: z.string(),
  threadId: z.uuid(),
  itemId: z.uuid().nullable(),
  itemTitle: z.string().nullable(),
  theme: z.string(),
  outcome: wikiAgentReplyOutcomeSchema,
  text: z.string(),
  choices: z.array(z.string()),
  status: wikiAgentReplyStatusSchema,
  edited: z.boolean(),
  sentAt: z.string().nullable(),
});
export type WikiResolutionReply = z.infer<typeof wikiResolutionReplySchema>;

/** `GET …/resolution-runs/:run` (D4, D7): the run as its creator reviews it. */
export const wikiResolutionRunSchema = z.object({
  id: z.uuid(),
  changeSetId: z.uuid(),
  revision: z.int().min(1),
  status: wikiResolutionRunStatusSchema,
  createdAt: z.string(),
  endedAt: z.string().nullable(),
  counts: wikiResolutionCountsSchema,
  replies: z.array(wikiResolutionReplySchema),
  /** Listed threads the agent did not answer: the author can reply by hand. */
  unanswered: z.array(
    z.object({ ref: z.string(), threadId: z.uuid(), itemTitle: z.string().nullable() }),
  ),
  /** The items the run changed, each undoable while unchanged since the run. */
  items: z.array(
    z.object({ itemId: z.uuid(), title: z.string(), undoable: z.boolean(), undone: z.boolean() }),
  ),
});
export type WikiResolutionRun = z.infer<typeof wikiResolutionRunSchema>;

/** `PATCH …/agent-replies/:id`: the creator's edit of a draft (D4). */
export const editAgentReplyRequestSchema = z.object({
  text: wikiAgentReplyTextSchema,
  choices: z.array(wikiAgentReplyChoiceSchema).max(WIKI_AGENT_REPLY_CHOICES_MAX).optional(),
});
export type EditAgentReplyRequest = z.input<typeof editAgentReplyRequestSchema>;

/** `POST …/resolution-runs/:run/undo-item` (D4). */
export const undoResolutionItemRequestSchema = z.object({ itemId: z.uuid() });
export type UndoResolutionItemRequest = z.infer<typeof undoResolutionItemRequestSchema>;

/** A send action's result: what it sent. */
export const sendAgentRepliesResultSchema = z.object({ sent: z.int().min(0) });

/**
 * Stored with the server-composed user message of a run's turn (D2): the run card. IDs and the
 * thread count only; the reviewers' words are in the message text.
 */
export const WIKI_RESOLUTION_DATA_NAME = "wiki-resolution";
export const WIKI_RESOLUTION_PART = `data-${WIKI_RESOLUTION_DATA_NAME}` as const;
export const wikiResolutionPartSchema = z.object({
  type: z.literal(WIKI_RESOLUTION_PART),
  data: z.object({
    runId: z.uuid(),
    changeSetId: z.uuid(),
    revision: z.int().min(1),
    threadCount: z.int().min(1).max(WIKI_RESOLVE_THREADS_MAX),
  }),
});
export type WikiResolutionPart = z.infer<typeof wikiResolutionPartSchema>;
